import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { usePublic } from "@/pages/public/PublicLayout";
import { fileUrl } from "@/lib/api";
import { ArrowLeft, ChevronRight } from "lucide-react";

const UI = {
  it: { back: "Menù", items: "voci", noSections: "Nessuna sezione.", notFound: "Menù non trovato.", cover: "Coperto" },
  en: { back: "Menus", items: "items", noSections: "No sections.", notFound: "Menu not found.", cover: "Cover charge" },
  fr: { back: "Menus", items: "articles", noSections: "Aucune section.", notFound: "Menu introuvable.", cover: "Couvert" },
  de: { back: "Menüs", items: "Einträge", noSections: "Keine Abschnitte.", notFound: "Menü nicht gefunden.", cover: "Gedeck" },
  es: { back: "Menús", items: "artículos", noSections: "Ninguna sección.", notFound: "Menú no encontrado.", cover: "Cubierto" },
  pt: { back: "Menus", items: "itens", noSections: "Nenhuma secção.", notFound: "Menu não encontrado.", cover: "Couvert" },
};

const CategorySelect = () => {
  const { data, theme, lang, slug } = usePublic();
  const { menuId } = useParams();
  const navigate = useNavigate();
  const tr = UI[lang] || UI.it;
  const menu = (data.menus || []).find((m) => m.id === menuId);
  if (!menu) return <p style={{ color: theme.muted }} className="text-center py-10">{tr.notFound}</p>;

  const all = menu.categories || [];
  const topCats = all.filter((c) => !c.parent_id);
  const countFor = (c) => {
    const own = (c.dishes || []).length;
    const kids = all.filter((k) => k.parent_id === c.id).reduce((s, k) => s + (k.dishes || []).length, 0);
    return own + kids;
  };

  return (
    <div>
      <button onClick={() => navigate(`/m/${slug}`)} data-testid="cat-back" className="flex items-center gap-1.5 text-sm mb-4" style={{ color: theme.muted }}>
        <ArrowLeft className="w-4 h-4" /> {tr.back}
      </button>
      <h2 className="text-3xl mb-1" style={{ fontFamily: theme.heading, color: theme.text }}>{(lang !== "it" && menu.translations?.[lang]?.name) || menu.name}</h2>
      <div className="h-px w-14 mb-6" style={{ background: `linear-gradient(90deg, ${theme.accent}, transparent)` }} />

      <div className="space-y-4">
        {topCats.map((c, i) => (
          <motion.button
            key={c.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: i * 0.06 }}
            onClick={() => navigate(`/m/${slug}/menu/${menuId}/${c.id}`)}
            data-testid={`cat-card-${c.id}`}
            className="group glow-hover relative w-full h-48 sm:h-56 rounded-2xl overflow-hidden text-left"
            style={{ border: `1px solid ${theme.accent}40`, "--glow": theme.accent, "--glow-soft": `${theme.accent}55` }}
          >
            {c.cover_image ? (
              <img src={fileUrl(c.cover_image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 group-active:scale-105" loading="lazy" />
            ) : (
              <div className="absolute inset-0" style={{ backgroundColor: theme.surface }} />
            )}
            <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${theme.bg}f0 0%, ${theme.bg}55 55%, transparent 100%)` }} />
            <div className="absolute inset-0 p-5 flex flex-col justify-end">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-medium leading-tight" style={{ fontFamily: theme.heading, color: theme.text }}>{(lang !== "it" && c.translations?.[lang]) || c.name}</span>
                <ChevronRight className="w-5 h-5 ml-auto transition-transform group-hover:translate-x-1" style={{ color: theme.accent }} />
              </div>
              {((lang !== "it" && c.desc_translations?.[lang]) || c.description) && (
                <p className="text-sm mt-1 leading-snug" style={{ color: theme.muted }}>{(lang !== "it" && c.desc_translations?.[lang]) || c.description}</p>
              )}
              <span className="text-xs mt-1" style={{ color: theme.muted }}>{countFor(c)} {tr.items}</span>
            </div>
          </motion.button>
        ))}
        {topCats.length === 0 && <p className="text-center py-10" style={{ color: theme.muted }}>{tr.noSections}</p>}
      </div>

      {(() => {
        const mt = (lang !== "it" && menu.translations?.[lang]) || {};
        const coverText = mt.cover_text || menu.cover_text;
        const notes = mt.notes || menu.notes;
        const hasCover = menu.cover_charge != null || (coverText && coverText.trim());
        const hasNotes = notes && notes.trim();
        if (!hasCover && !hasNotes) return null;
        return (
          <div className="mt-10 pt-6" style={{ borderTop: `1px solid ${theme.accent}33` }} data-testid="menu-footer-info">
            {hasCover && (
              <p className="text-sm" data-testid="menu-cover-line">
                <span style={{ color: theme.text }}>{(coverText && coverText.trim()) || tr.cover}</span>
                {menu.cover_charge != null && <span style={{ color: theme.accent }}> · € {Number(menu.cover_charge).toFixed(2)}</span>}
              </p>
            )}
            {hasNotes && <p className="text-sm mt-3 leading-relaxed whitespace-pre-line" style={{ color: theme.muted }} data-testid="menu-notes-line">{notes}</p>}
          </div>
        );
      })()}
    </div>
  );
};

export default CategorySelect;
